import { useState, useEffect } from 'react';
import { functions } from '@/lib/firebase';
import { httpsCallable } from 'firebase/functions';

export interface LabOSUser {
  uid: string;
  email: string;
  display_name: string | null;
  role: string;
  is_active: boolean;
  last_login_at: string | null;
  access: Array<{ app_id: string; source: string }>;
}

interface UseLabOSUsersReturn {
  users: LabOSUser[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Custom hook to fetch users from LabOS using the same source as the labos repository.
 * Uses the adminListUsers callable function from Firebase.
 */
export function useLabOSUsers(): UseLabOSUsersReturn {
  const [users, setUsers] = useState<LabOSUser[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUsers = async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const adminListUsers = httpsCallable<Record<string, never>, LabOSUser[]>(functions, 'adminListUsers');
      const result = await adminListUsers({});
      setUsers(result.data || []);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch users from LabOS';
      setError(errorMessage);
      console.error('[useLabOSUsers] Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  return {
    users,
    loading,
    error,
    refetch: fetchUsers,
  };
}
